import * as React from 'react';
import { z } from 'zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FormPageLayout } from '../../../../../components/layout/FormPageLayout';
import { Card, CardContent } from '../../../../../components/ui/Card';
import { FormField, Input } from '../../../../../components/ui/FormField';
import { FormSelect } from '../../../../../components/ui/FormSelect';
import { Button } from '../../../../../components/ui/Button';
import { useCustomers } from '../../../../../features/customers/hooks';
import { useAddNominee } from '../../../../../features/surety/hooks';
import { useToast } from '../../../../../components/ui/Toast';
import { useDebounce, usePageTitle } from '../../../../../lib/hooks';
import { defineFormPage, useFormPage } from '@repo/form';
import { Banner } from '../../../../../components/ui/Banner';
import type { CustomerListItem } from '../../../../../features/customers/types';

export const Route = createFileRoute('/_auth/surety/$id/nominees/new')({
  component: AddNomineePage,
});

const AddNomineeValidator = z.object({
  relationship: z.string().min(1, 'Relationship is required'),
  pan: z.string().regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN Card format (e.g. ABCDE1234F)'),
  phone: z.string().regex(/^[6-9]\d{9}$/, 'Invalid 10-digit Indian phone number'),
});

type FormValues = z.infer<typeof AddNomineeValidator>;

const nomineeFormConfig = defineFormPage<FormValues>({
  resource: 'nominees',
  defaultValues: {
    relationship: 'Spouse',
    pan: '',
    phone: '',
  },
  schema: AddNomineeValidator,
});

function AddNomineePage() {
  usePageTitle('Add Nominee Guarantor');
  const navigate = useNavigate();
  const { id: requestId } = Route.useParams();
  const { toast } = useToast();
  const [customerQuery, setCustomerQuery] = React.useState('');
  const debouncedQuery = useDebounce(customerQuery, 300);
  const { data: customersResponse, isLoading: searchingCustomers } = useCustomers({
    page: 1,
    limit: 5,
    query: debouncedQuery || undefined,
  });

  const [selectedCustomer, setSelectedCustomer] = React.useState<CustomerListItem | null>(null);
  const addNomineeMutation = useAddNominee();

  const {
    form,
    isSubmitting,
    handleSubmit,
    fieldError: fe,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(nomineeFormConfig, addNomineeMutation, {
    routeId: `surety/${requestId}/nominees/new`,
    onBeforeSubmit: () => {
      if (!selectedCustomer) {
        toast({
          type: 'error',
          title: 'Failed to Add Nominee',
          description: 'Please search and select an active customer',
        });
        throw new Error('Please select a customer');
      }
      return {
        nomineeMembershipId: selectedCustomer.id,
        nomineeCustomerId: selectedCustomer.id,
      };
    },
  });

  const onFormSubmit = async (e: React.FormEvent) => {
    try {
      await handleSubmit(e);
      clearDraft();
      setSelectedCustomer(null);
      setCustomerQuery('');
      form.reset();
      toast({
        type: 'success',
        title: 'Nominee Added',
        description: 'New nominee has been successfully added to this request.',
      });
      void navigate({ to: '/surety/$id', params: { id: requestId } });
    } catch (err) {
      console.error('Failed to add nominee:', err);
      toast({
        type: 'error',
        title: 'Failed to Add Nominee',
        description: (err instanceof Error ? err.message : '') || 'Nominee could not be added.',
      });
    }
  };

  return (
    <FormPageLayout
      title="Add Nominee Guarantor"
      subtitle="Search for an active Customer profile to nominate them as a surety guarantor."
      backTo={{ to: "/surety/$id", params: { id: requestId } }}
    >
      <Card className="shadow-sm max-w-xl mx-auto">
        <CardContent className="p-6">
          <form
            onSubmit={onFormSubmit}
            className="space-y-4"
          >
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <FormField label="Search Customer Profile" required>
              <div className="relative">
                <Input
                  value={customerQuery}
                  onChange={(e) => {
                    setCustomerQuery(e.target.value);
                    if (selectedCustomer) setSelectedCustomer(null);
                  }}
                  placeholder="Type name, phone or customer no..."
                  className="w-full"
                />
                {searchingCustomers && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted">
                    Searching...
                  </span>
                )}
              </div>

              {customerQuery && !selectedCustomer && customersResponse && (
                <div className="mt-1 max-h-40 overflow-y-auto bg-surface-3 border border-border rounded-lg divide-y divide-border shadow-lg">
                  {customersResponse.data.length === 0 ? (
                    <div className="p-3 text-center text-xs text-text-muted">
                      No active customers found
                    </div>
                  ) : (
                    customersResponse.data.map((c: CustomerListItem) => (
                      <Button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setSelectedCustomer(c);
                          setCustomerQuery(c.name);
                        }}
                        className="w-full text-left p-3 hover:bg-surface-2 transition-colors flex flex-col text-xs"
                      >
                        <span className="font-semibold text-text-primary">{c.name}</span>
                        <span className="text-text-muted mt-0.5">
                          Phone: {c.mobile} | No: {c.customerCode}
                        </span>
                      </Button>
                    ))
                  )}
                </div>
              )}
            </FormField>

            {selectedCustomer && (
              <div className="p-3 bg-surface-2 border border-border rounded-lg text-xs space-y-1">
                <p className="font-semibold text-text-primary">Selected Profile Details</p>
                <p className="text-text-secondary">Name: {selectedCustomer.name}</p>
                <p className="text-text-secondary">Default Phone: {selectedCustomer.mobile}</p>
              </div>
            )}

            <form.Field
              name="relationship"
              children={(field: any) => (
                <FormSelect
                  label="Relationship"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  options={[
                    { value: 'Spouse', label: 'Spouse' },
                    { value: 'Father', label: 'Father' },
                    { value: 'Mother', label: 'Mother' },
                    { value: 'Brother', label: 'Brother' },
                    { value: 'Sister', label: 'Sister' },
                    { value: 'Friend', label: 'Friend' },
                    { value: 'Other', label: 'Other' },
                  ]}
                  required
                  error={fe(field.state.meta)}
                />
              )}
            />

            <form.Field
              name="pan"
              children={(field: any) => (
                <FormField
                  label="PAN Card Number"
                  required
                  error={fe(field.state.meta)}
                  hint="Format: 5 capital letters, 4 digits, 1 capital letter (e.g. ABCDE1234F)"
                >
                  <Input
                    value={field.state.value}
                    onChange={(e: any) => field.handleChange(e.target.value)}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                </FormField>
              )}
            />

            <form.Field
              name="phone"
              children={(field: any) => (
                <FormField
                  label="Nominee Phone Number"
                  required
                  error={fe(field.state.meta)}
                  hint="Indian 10-digit phone number starting with 6-9"
                >
                  <Input
                    value={field.state.value}
                    onChange={(e: any) => field.handleChange(e.target.value)}
                    placeholder={selectedCustomer?.mobile || '9876543210'}
                    maxLength={10}
                  />
                </FormField>
              )}
            />

            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button type="button" variant="secondary" onClick={() => void navigate({ to: '/surety/$id', params: { id: requestId } })}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={!selectedCustomer}
              >
                Add Nominee
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </FormPageLayout>
  );
}
